<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { ChevronLeft, ChevronRight } from 'lucide-vue-next';
import { Link } from '@inertiajs/vue3';
import { useSignupStorage } from '@/composables/useSignupStorage';
import { availableDates, contactUs, servicePricing } from '@/routes';

const { signup } = useSignupStorage();

defineEmits<{
    (e: 'back'): void;
    (e: 'next'): void;
}>();

const today = new Date();
today.setHours(0, 0, 0, 0);

const currentMonth = ref(today.getMonth());
const currentYear = ref(today.getFullYear());
const loading = ref(false);

const seasonStart = ref<string>('');
const blockedDates = ref<Set<string>>(new Set());
const atCapacityDates = ref<Set<string>>(new Set());
const availableDays = ref<number[]>([]);
const disabledPricingDays = ref<number[]>([]);
const basePrice = ref<number>(0);
const tooLarge = ref(false);

const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

const dayHeaders = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const currentMonthName = computed(() => monthNames[currentMonth.value]);

const monthRange = computed(() => {
    const first = new Date(currentYear.value, currentMonth.value, 1);
    const last = new Date(currentYear.value, currentMonth.value + 1, 0);
    const formatDate = (d: Date) => {
        return `${monthNames[d.getMonth()].slice(0, 3)} ${d.getDate()}, ${d.getFullYear()}`;
    };
    return `${formatDate(first)} – ${formatDate(last)}`;
});

function isWeekend(colIndex: number): boolean {
    return colIndex >= 5;
}

// Column index (0=Mon..4=Fri) to ISO day (1=Mon..5=Fri)
function isRestrictedColumn(colIndex: number): boolean {
    if (availableDays.value.length === 0) {
        return false;
    }
    const isoDay = colIndex + 1;
    return !availableDays.value.includes(isoDay);
}

// Mon/Tue = -$5, Wed = 0, Thu/Fri = +$5
const dayAdjustmentMap: Record<number, number> = { 1: -5, 2: -5, 3: 0, 4: 5, 5: 5 };

function dayAdjustment(dow: number): number {
    if (disabledPricingDays.value.includes(dow)) {
        return 0;
    }
    return dayAdjustmentMap[dow] ?? 0;
}

const applyDynamicPricing = computed(() => {
    if (availableDays.value.length === 0) {
        return true;
    }
    const uniqueAdjustments = [
        ...new Set(availableDays.value.map((day) => dayAdjustmentMap[day] ?? 0)),
    ];
    return uniqueAdjustments.length > 1;
});

type CalendarDay = {
    date: Date;
    day: number;
    isCurrentMonth: boolean;
    isAvailable: boolean;
    isPast: boolean;
    isBeforeSeason: boolean;
    isBlocked: boolean;
    isAtCapacity: boolean;
    isWeekend: boolean;
    isRestricted: boolean;
    dateString: string;
    price: number | null;
};

const calendarWeeks = computed((): CalendarDay[][] => {
    const year = currentYear.value;
    const month = currentMonth.value;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    let startDow = firstDay.getDay();
    if (startDow === 0) {
        startDow = 7;
    }
    const prefixDays = startDow - 1;

    const days: CalendarDay[] = [];

    for (let i = prefixDays; i > 0; i--) {
        const d = new Date(year, month, 1 - i);
        days.push(buildDay(d, false));
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
        const d = new Date(year, month, i);
        days.push(buildDay(d, true));
    }

    const remaining = 7 - (days.length % 7);
    if (remaining < 7) {
        for (let i = 1; i <= remaining; i++) {
            const d = new Date(year, month + 1, i);
            days.push(buildDay(d, false));
        }
    }

    const weeks: CalendarDay[][] = [];
    for (let i = 0; i < days.length; i += 7) {
        weeks.push(days.slice(i, i + 7));
    }
    return weeks;
});

function buildDay(date: Date, isCurrentMonth: boolean): CalendarDay {
    const dateString = formatDateISO(date);
    const dow = date.getDay();
    const weekend = dow === 0 || dow === 6;
    const isPast = date < today;
    const isBeforeSeason = seasonStart.value
        ? dateString < seasonStart.value
        : false;
    const isBlocked = blockedDates.value.has(dateString);

    // Map JS day (0=Sun..6=Sat) to ISO day (1=Mon..5=Fri) for availableDays comparison
    const isoDay = dow === 0 ? 7 : dow;
    const isRestrictedDay =
        availableDays.value.length > 0 && !availableDays.value.includes(isoDay);

    // Only show red for at-capacity if the day is the right weekday and not past/before season
    const isEligibleDay =
        !weekend && !isPast && !isBeforeSeason && !isRestrictedDay;
    const isAtCapacity = isEligibleDay && atCapacityDates.value.has(dateString);

    const isAvailable = isEligibleDay && !isBlocked && !isAtCapacity;

    let price: number | null = null;
    if (isAvailable && basePrice.value > 0) {
        const adjustment = applyDynamicPricing.value ? dayAdjustment(dow) : 0;
        price = basePrice.value + adjustment;
    }

    return {
        date,
        day: date.getDate(),
        isCurrentMonth,
        isAvailable,
        isPast,
        isBeforeSeason,
        isBlocked,
        isAtCapacity,
        isWeekend: weekend,
        isRestricted: isRestrictedDay,
        dateString,
        price,
    };
}

function formatDateISO(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function prevMonth(): void {
    if (currentMonth.value === 0) {
        currentMonth.value = 11;
        currentYear.value--;
    } else {
        currentMonth.value--;
    }
}

function nextMonth(): void {
    if (currentMonth.value === 11) {
        currentMonth.value = 0;
        currentYear.value++;
    } else {
        currentMonth.value++;
    }
}

function selectDay(day: CalendarDay): void {
    if (!day.isAvailable || day.price === null) {
        return;
    }
    signup.start_date = day.dateString;

    // Store the calendar-displayed price directly so the review screen
    // always matches what the customer saw. Tax rate comes from the
    // base pricing fetch that already ran on mount.
    const displayedPrice = day.price;
    signup.price = displayedPrice;
    signup.subtotal_mowing = displayedPrice;
    signup.quote_price = displayedPrice;
    signup.cost_service = displayedPrice;

    const taxRate = signup.tax_rate;
    if (taxRate > 0) {
        const tax = Math.round(displayedPrice * taxRate) / 100;
        signup.tax = tax;
        signup.calculated_tax = tax;
        signup.total_mowing = Math.round((displayedPrice + tax) * 100) / 100;
    } else {
        signup.tax = 0;
        signup.calculated_tax = 0;
        signup.total_mowing = displayedPrice;
    }
}

function isSelected(day: CalendarDay): boolean {
    return signup.start_date === day.dateString;
}

const canGoBack = computed(() => {
    return (
        currentYear.value > today.getFullYear() ||
        (currentYear.value === today.getFullYear() &&
            currentMonth.value > today.getMonth())
    );
});

function csrfToken(): string {
    return decodeURIComponent(
        document.cookie.match(/XSRF-TOKEN=([^;]+)/)?.[1] ?? '',
    );
}

async function fetchAvailableDates(): Promise<void> {
    if (!signup.zip) {
        return;
    }

    loading.value = true;

    try {
        const response = await fetch(
            availableDates.url({ zipCode: signup.zip }),
            {
                headers: {
                    Accept: 'application/json',
                    'X-XSRF-TOKEN': csrfToken(),
                },
            },
        );

        if (response.ok) {
            const data = await response.json();
            seasonStart.value = data.seasonStart ?? '';
            const blocked = [
                ...(data.blockedDates ?? []),
                ...(data.holidays ?? []),
            ];
            blockedDates.value = new Set(blocked);
            atCapacityDates.value = new Set(data.atCapacityDates ?? []);
            availableDays.value = (data.availableDays ?? []).map(Number);
            disabledPricingDays.value = (data.dynamicPricingDisabledDays ?? []).map(Number);

            if (seasonStart.value) {
                const [y, m] = seasonStart.value.split('-').map(Number);
                if (
                    y > currentYear.value ||
                    (y === currentYear.value && m - 1 > currentMonth.value)
                ) {
                    currentYear.value = y;
                    currentMonth.value = m - 1;
                }
            }
        }
    } catch {
        // fallback — all weekdays available
    } finally {
        loading.value = false;
    }
}

function storePricing(pricing: Record<string, unknown>): void {
    signup.price = Number(pricing.price) || 0;
    signup.quote_price =
        Number(pricing.subtotalMowing) || Number(pricing.price) || 0;
    signup.tax = Number(pricing.tax) || 0;
    signup.tax_rate = Number(pricing.taxRate) || 0;
    signup.cost_service = Number(pricing.costService) || 0;
    signup.calculated_tax = Number(pricing.calculatedTax) || 0;
    signup.subtotal_mowing = Number(pricing.subtotalMowing) || 0;
    signup.total_mowing = Number(pricing.totalMowing) || 0;
}

async function fetchBasePricing(): Promise<void> {
    if (!signup.frequency || !signup.mow_areas || !signup.zip) {
        return;
    }

    try {
        const response = await fetch(servicePricing.url(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-XSRF-TOKEN': csrfToken(),
            },
            body: JSON.stringify({
                frequency: signup.frequency,
                mow_areas: signup.mow_areas,
                zip: signup.zip,
                lot_size: Number(signup.melissa?.size) || 0,
                living_space: Number(signup.melissa?.livingSpace) || 0,
                lawn_sqft: Number(signup.analysis?.lawnSqft) || 0,
                building_area: Number(signup.melissa?.buildingArea) || 0,
                bag_grass: signup.optional_services.includes('bag_grass'),
                corner_lot: signup.corner_lot === 'yes',
            }),
        });

        if (response.ok) {
            const data = await response.json();
            tooLarge.value = data.tooLarge === true;
            const pricing = data.pricing ?? {};
            basePrice.value =
                Number(pricing.subtotalMowing) || Number(pricing.price) || 0;
            storePricing(pricing);
            if (data.turfSize) {
                signup.lawn_sqft = data.turfSize;
            }
        }
    } catch {
        // pricing unavailable
    }
}

function isDateValid(dateString: string): boolean {
    const date = new Date(dateString + 'T00:00:00');
    if (isNaN(date.getTime())) {
        return false;
    }

    const dow = date.getDay();
    const weekend = dow === 0 || dow === 6;
    if (weekend) {
        return false;
    }

    if (date < today) {
        return false;
    }

    if (seasonStart.value && dateString < seasonStart.value) {
        return false;
    }

    if (blockedDates.value.has(dateString)) {
        return false;
    }

    if (atCapacityDates.value.has(dateString)) {
        return false;
    }

    const isoDay = dow === 0 ? 7 : dow;
    if (availableDays.value.length > 0 && !availableDays.value.includes(isoDay)) {
        return false;
    }

    return true;
}

onMounted(async () => {
    signup.start_date = '';
    signup.optional_services = [];

    await Promise.all([fetchAvailableDates(), fetchBasePricing()]);
});

watch(
    () => [signup.frequency, signup.mow_areas],
    () => {
        fetchBasePricing();
    },
);
</script>

<template>
    <div v-if="tooLarge">
        <div class="mb-[61px]">
            <div
                class="overflow-hidden rounded-xl border border-gray-200 px-8 py-12 text-center shadow-sm"
            >
                <h3 class="text-2xl font-bold text-mm-green">
                    Your property is larger than expected
                </h3>
                <p class="mx-auto mt-4 max-w-md text-mm-dark">
                    Please contact our team for custom pricing options.
                </p>
                <Link
                    :href="contactUs.url()"
                    class="mt-8 inline-block rounded-md bg-mm-green px-[36.5px] py-[14.6px] text-[14px] font-bold tracking-[1.6px] text-white uppercase transition-colors hover:bg-mm-green-dark"
                >
                    Contact Us
                </Link>
            </div>
        </div>
        <div class="flex items-center justify-center gap-4">
            <button
                type="button"
                class="cursor-pointer rounded-md border-2 border-gray-300 px-[36.5px] py-[14.6px] text-[14px] font-bold tracking-[1.6px] text-mm-dark uppercase transition-colors hover:border-gray-400"
                @click="$emit('back')"
            >
                Back
            </button>
        </div>
    </div>
    <div v-else>
        <div class="mb-[61px]">
            <label
                class="text-xs font-bold tracking-[1.4px] text-mm-dark uppercase"
            >
                Select your mow day & start date<span class="text-mm-green"
                    >*</span
                >
            </label>

            <hr class="my-3 border-gray-300" />
            <p class="text-sm text-gray-600">
                If your requested day is unavailable, call us at
                <a href="tel:8884386697" class="font-semibold text-mm-green hover:underline">888-438-6697</a>
            </p>

            <div
                class="mt-4 overflow-hidden rounded-xl border border-gray-200 shadow-sm"
            >
                <div class="px-6 pt-5 pb-4">
                    <h3 class="text-lg font-bold text-mm-dark">
                        {{ currentMonthName }} {{ currentYear }}
                    </h3>
                    <p class="text-xs text-gray-500">{{ monthRange }}</p>
                </div>

                <div class="flex items-center gap-2 px-6 pb-4">
                    <button
                        type="button"
                        class="shrink-0 rounded-md border border-gray-300 p-1.5 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                        :disabled="!canGoBack"
                        @click="prevMonth"
                    >
                        <ChevronLeft class="size-4 text-gray-600" />
                    </button>
                    <span
                        class="w-full rounded-md border border-gray-300 px-3 py-1.5 text-center text-sm font-medium text-mm-dark"
                    >
                        {{ currentMonthName }}
                    </span>
                    <button
                        type="button"
                        class="shrink-0 rounded-md border border-gray-300 p-1.5 transition-colors hover:bg-gray-50"
                        @click="nextMonth"
                    >
                        <ChevronRight class="size-4 text-gray-600" />
                    </button>
                </div>

                <table class="w-full border-collapse">
                    <thead>
                        <tr>
                            <th
                                v-for="(header, colIdx) in dayHeaders"
                                :key="colIdx"
                                class="border-t border-gray-200 py-2 text-center text-xs font-bold uppercase"
                                :class="
                                    isWeekend(colIdx) ||
                                    isRestrictedColumn(colIdx)
                                        ? 'bg-gray-100 text-gray-400'
                                        : 'bg-white text-gray-500'
                                "
                            >
                                {{ header }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(week, weekIdx) in calendarWeeks"
                            :key="weekIdx"
                        >
                            <td
                                v-for="(day, colIdx) in week"
                                :key="colIdx"
                                class="h-[68px] w-[14.28%] border-t border-gray-200 align-top sm:h-[88px]"
                                :class="[
                                    day.isAtCapacity
                                        ? 'bg-red-50'
                                        : day.isWeekend || day.isRestricted
                                          ? 'bg-gray-100'
                                          : 'bg-white',
                                    day.isAvailable ? 'cursor-pointer' : '',
                                ]"
                                @click="selectDay(day)"
                            >
                                <div
                                    class="flex h-full flex-col p-1 sm:p-2"
                                    :class="
                                        isSelected(day)
                                            ? 'border-2 border-mm-green bg-mm-green/10'
                                            : ''
                                    "
                                >
                                    <span
                                        class="text-xs font-medium sm:text-sm"
                                        :class="[
                                            day.isAtCapacity
                                                ? 'text-red-400'
                                                : !day.isAvailable
                                                  ? 'text-gray-300'
                                                  : !day.isCurrentMonth
                                                    ? 'text-gray-400'
                                                    : 'text-mm-dark',
                                        ]"
                                    >
                                        {{ day.day }}
                                    </span>
                                    <span
                                        v-if="
                                            day.isAvailable &&
                                            day.price !== null
                                        "
                                        class="mt-0.5 text-xs font-bold text-mm-dark sm:text-sm"
                                    >
                                        ${{ day.price }}
                                    </span>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <p class="mt-3 text-xs text-gray-400 italic">
                Mow day subject to change based on availability.
            </p>
            <p class="mt-1 text-xs text-red-500 italic">
                Red dates are currently at full capacity and unavailable for new
                service.
            </p>
        </div>

        <div class="flex items-center justify-center gap-4">
            <button
                type="button"
                class="cursor-pointer rounded-md border-2 border-gray-300 px-[36.5px] py-[14.6px] text-[14px] font-bold tracking-[1.6px] text-mm-dark uppercase transition-colors hover:border-gray-400"
                @click="$emit('back')"
            >
                Back
            </button>
            <button
                type="button"
                class="cursor-pointer rounded-md bg-mm-green px-[36.5px] py-[14.6px] text-[14px] font-bold tracking-[1.6px] text-white uppercase transition-colors hover:bg-mm-green-dark disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="!signup.start_date"
                @click="$emit('next')"
            >
                Next
            </button>
        </div>
    </div>
</template>
